// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import ActivityItemCreate from 'app/common/react/components/Activities/ActivityItemCreate/ActivityItemCreate';
import AggregatedCommentContainer from 'app/common/react/components/Comments/AggregatedComments/AggregatedCommentContainer';
import ConnectViewer from 'app/common/react/components/common/ConnectViewer/ConnectViewer';
import i18n from 'app/common/lib/i18n';
import TextButton from 'app/common/react/components/Activities/TextButton';
import type { AggregatedComment } from 'app/packages/pinterest-flow-types/comment';
import type { AuthViewer } from 'app/packages/pinterest-flow-types/viewer';
import type { State as ReduxState } from 'app/common/redux/types';
// $FlowFixMe Flow v0.104.0 upgrade supression.
import type { ThunkAction } from 'redux-thunk';
import { interpolateNamedTemplate } from 'app/common/lib/react/interpolate';
import { pinUpdateCommentCount } from 'app/common/redux/actions/pin/comment';
import { track } from 'app/common/lib/react/analytics';
import { type CloseupViewType } from 'app/common/react/components/closeup/types';
import { updateUserDidItDataCommentCount } from 'app/common/redux/actions/user_did_it_data/did_it';
import { withFeaturedComment } from 'app/common/react/components/Comments/FeaturedCommentContext';
import withResource, { type Resource } from 'app/common/lib/react/withResource';
import {
  COMMENT_OBJECT_TYPES,
  type CommentObjectType,
} from 'app/common/redux/reducers/aggregatedComments';
import {
  createAggregatedComment,
  type AggregatedCommentCreateData,
} from 'app/common/redux/actions/aggregatedComments/create';
import {
  updateAggregatedComment,
  type AggregatedCommentUpdateData,
} from 'app/common/redux/actions/aggregatedComments/update';
import { bindActionCreators, compose, type Dispatch } from 'redux';
import { Box, Spinner, Text } from 'gestalt';
import { type FeedItemKey, FEED_TYPE } from 'app/common/redux/reducers/feeds';

export const INITIAL_COMMENT_SIZE = 2;

export const MAX_DEPTH = 2;

type OwnProps = {|
  comment?: Object,
  commentCount: number,
  depth?: number,
  didItOwnerId?: string,
  initialLoadCount?: number,
  isManagedView?: boolean,
  objectId: string,
  objectType: CommentObjectType,
  onRef?: () => void,
  parentObjectId?: string,
  pinId?: string,
  pinDomainOwnerId?: string,
  privatePin?: boolean,
  replyToName?: string,
  reverseOrder?: boolean,
  variant: CloseupViewType,
|};

type HOCProps = {|
  createComment: (AggregatedCommentCreateData) => Promise<AggregatedComment>,
  featuredCommentId?: string,
  feedResource: Resource<Array<Object>>,
  updateComment: (AggregatedCommentUpdateData) => void,
  updateCommentCount: (id: string, increment: 1 | -1) => void,
|};

type ReduxProps = {|
  feedItems?: ?Array<FeedItemKey>,
|};

type Props = {| ...OwnProps, ...HOCProps, ...ReduxProps |};

type State = {|
  inCreationMode: boolean,
  isLoadingNewCreatedComment: boolean,
|};

export class AggregatedCommentListUnified extends Component<Props, State> {
  static defaultProps = {
    reverseOrder: false,
  };

  state = {
    inCreationMode: false,
    isLoadingNewCreatedComment: false,
  };

  handleAddComment = (createData: { contentMarkdown: string, tags: string }) => {
    const { createComment, objectId, objectType, pinId, updateCommentCount } = this.props;

    this.toggleCreationMode();
    this.setState({ isLoadingNewCreatedComment: true });

    createComment({
      objectType,
      objectId,
      pinId,
      tags: createData.tags,
      text: createData.contentMarkdown,
    }).then(() => this.setState({ isLoadingNewCreatedComment: false }));
    updateCommentCount(pinId || objectId, 1);
    if (objectType === COMMENT_OBJECT_TYPES.pin) {
      track(PCONST.thrift.EventType.PIN_COMMENT, {
        objectId: this.props.pinId,
      });
    }
  };

  toggleCreationMode = () => this.setState({ inCreationMode: !this.state.inCreationMode });

  renderComments = () => {
    const {
      didItOwnerId,
      depth = 0,
      feedItems,
      feedResource: { isLoaded },
      isManagedView,
      objectId,
      objectType,
      pinDomainOwnerId,
      pinId,
      variant,
    } = this.props;
    const { isLoadingNewCreatedComment } = this.state;

    return (
      <Box>
        {feedItems &&
          feedItems.length > 0 && (
            <ConnectViewer>
              {(viewer: AuthViewer) => (
                <Box>
                  {feedItems.map((commentData, commentIndex) => (
                    <Box marginBottom={2} key={commentData.id}>
                      <AggregatedCommentContainer
                        aggregatedCommentId={commentData.id}
                        depth={depth + 1}
                        didItOwnerId={didItOwnerId}
                        isManagedView={isManagedView}
                        objectType={objectType}
                        parentObjectId={pinId || objectId} // We depend on the pinId for updating comment counts, while the object id refers to the aggregated pin data id
                        pinDomainOwnerId={pinDomainOwnerId}
                        viewerId={viewer.id}
                        variant={variant}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </ConnectViewer>
          )}
        <Spinner
          accessibilityLabel={i18n._('Comments loading')}
          show={!isLoaded || isLoadingNewCreatedComment}
        />
      </Box>
    );
  };

  renderLeaveAComment = () => {
    const { objectType, parentObjectId, privatePin } = this.props;
    const { inCreationMode } = this.state;
    return (
      <Box display="flex" marginTop={6}>
        <Box flex="grow" data-test-id="add-comment">
          <ActivityItemCreate
            hideAddObjects
            onAddItem={this.handleAddComment}
            onCancelItem={this.toggleCreationMode}
            onEnter={this.toggleCreationMode}
            objectType={objectType}
            onAddPinModeChange={() => {}}
            parentObjectId={parentObjectId}
            privatePin={privatePin}
            replyToObject={null}
            statusType={inCreationMode ? 'textInflightCreation' : 'textStaticDisplay'}
          />
        </Box>
      </Box>
    );
  };

  render() {
    const {
      commentCount,
      feedItems,
      feedResource: { isLoaded, isAtEnd, fetchMore },
      isManagedView,
      objectType,
      variant,
    } = this.props;

    const { isLoadingNewCreatedComment } = this.state;

    const moreCommentsAvailable =
      !!feedItems && isLoaded && commentCount > feedItems.length && !isAtEnd;
    const commentCountToShow = feedItems ? commentCount - feedItems.length : 0;

    const viewMoreComments = interpolateNamedTemplate(
      i18n.ngettext(
        '{{commentCount}} comment',
        '{{commentCount}} comments',
        commentCountToShow,
        'Show comments link with count',
      ),
      { commentCount: String(commentCountToShow) },
    );

    const isReply = objectType === COMMENT_OBJECT_TYPES.aggregatedComment;
    const showEmptyState =
      objectType === COMMENT_OBJECT_TYPES.pin &&
      ((feedItems && feedItems.length === 0) || commentCount === 0) &&
      isLoaded &&
      !isLoadingNewCreatedComment;

    const showDefaultStyles = variant === 'default';

    return (
      <Box
        data-test-id="aggregated-comment-list"
        marginTop={isReply && !showDefaultStyles ? 2 : 0}
        paddingY={isReply || showDefaultStyles ? 0 : 2}
      >
        {this.renderComments()}
        {moreCommentsAvailable && (
          <Box marginTop={3}>
            <TextButton text={viewMoreComments} handleClick={fetchMore} />
          </Box>
        )}
        {showEmptyState && (
          <Box paddingY={showDefaultStyles ? 0 : 2}>
            <Text size={showDefaultStyles ? 'sm' : 'md'}>
              {i18n._('Share feedback, ask a question or give a high five')}
            </Text>
          </Box>
        )}
        {/* Top level composer */}
        {objectType !== COMMENT_OBJECT_TYPES.aggregatedComment &&
          !isManagedView &&
          this.renderLeaveAComment()}
      </Box>
    );
  }
}

const mapDispatchToProps = (dispatch: Dispatch<ThunkAction>, ownProps: OwnProps) => {
  const { objectType } = ownProps;
  let updateCommentCount = (id, increment) => null;
  if (objectType === COMMENT_OBJECT_TYPES.pin) {
    updateCommentCount = bindActionCreators(pinUpdateCommentCount, dispatch);
  } else if (objectType === COMMENT_OBJECT_TYPES.didIt) {
    updateCommentCount = bindActionCreators(updateUserDidItDataCommentCount, dispatch);
  }

  return {
    createComment: bindActionCreators(createAggregatedComment, dispatch),
    updateComment: bindActionCreators(updateAggregatedComment, dispatch),
    updateCommentCount,
  };
};

function removeDuplicateKeys(comments, id) {
  if (!!comments) {
    return comments.filter(
      (obj, pos, arr) => arr.map((mapObj) => mapObj[id]).indexOf(obj[id]) === pos,
    );
  } else {
    return comments;
  }
}

export const CanonicalCommentList = compose(
  // $FlowFixMe: new errors found from flow 0.96 upgrade
  connect(
    (state: ReduxState, props: OwnProps): ReduxProps => {
      const {
        feeds: { [FEED_TYPE.AGGREGATED_COMMENTS]: aggregatedCommentsMap = {} },
      } = state;
      const aggregatedCommentsFeed = removeDuplicateKeys(
        aggregatedCommentsMap[props.objectId],
        'id',
      );
      return {
        feedItems: aggregatedCommentsFeed || [],
      };
    },
    mapDispatchToProps,
  ),
  withFeaturedComment,
  withResource({
    name: 'AggregatedCommentFeedResource',
    key: 'feedResource',
    options: (props) => ({
      objectId: props.objectId,
      page_size: props.initialLoadCount || INITIAL_COMMENT_SIZE,
      redux_normalize_feed: true,
      featured_ids: !!props.featuredCommentId ? [props.featuredCommentId] : null,
    }),
  }),
)(AggregatedCommentListUnified);

export const ReplyList = compose(
  // $FlowFixMe: new errors found from flow 0.96 upgrade
  connect(
    (state: ReduxState, props: OwnProps): ReduxProps => {
      const {
        feeds: { [FEED_TYPE.AGGREGATED_COMMENTS]: aggregatedCommentsMap = {} },
      } = state;
      const aggregatedCommentsFeed = aggregatedCommentsMap[props.objectId];
      return {
        feedItems: aggregatedCommentsFeed || [],
      };
    },
    mapDispatchToProps,
  ),
  withResource({
    name: 'AggregatedCommentReplyFeedResource',
    key: 'feedResource',
    options: (props) => ({
      objectId: props.objectId,
      page_size: props.initialLoadCount || INITIAL_COMMENT_SIZE,
      redux_normalize_feed: true,
    }),
  }),
)(AggregatedCommentListUnified);

export const DidItCommentList = compose(
  // $FlowFixMe: new errors found from flow 0.96 upgrade
  connect(
    (state: ReduxState, props: OwnProps): ReduxProps => {
      const {
        feeds: { [FEED_TYPE.AGGREGATED_COMMENTS]: aggregatedCommentsMap = {} },
      } = state;
      const aggregatedCommentsFeed = aggregatedCommentsMap[props.objectId];
      return {
        feedItems: aggregatedCommentsFeed || [],
      };
    },
    mapDispatchToProps,
  ),
  withResource({
    name: 'DidItCommentsResource',
    key: 'feedResource',
    options: (props) => ({
      objectId: props.objectId,
      page_size: props.initialLoadCount || INITIAL_COMMENT_SIZE,
      redux_normalize_feed: true,
    }),
  }),
)(AggregatedCommentListUnified);
